import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';


import { RoomService } from '@fitness-force/api';
import { ErrorMsgComponent } from '@fitness-force/errors';
import { CreateRoom, ListRoomResponse, Room } from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ROOM_LIST_STATE } from 'apps/ff-frontend/src/app/state/app.state';
import { ToastMessageService } from '@fitness-force/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { StyleClassModule } from 'primeng/styleclass';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'select-room',
  templateUrl: './select-room.component.html',
  styleUrls: ['./select-room.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    SelectModule,
    ButtonModule,
    InputTextModule,
    StyleClassModule,
    ErrorMsgComponent,
    TranslateModule
  ]
})
export class SelectRoomComponent implements OnInit {

  @Input('visible') addNewRoom: boolean = false;
  @Input('tenantId') tenantId: number = 0;
  @Input('roomId') roomId$ = new BehaviorSubject<number>(0);
  @Input('cssClass') cssClass: string = 'w-full text-sm';
  @Input() formGroup: UntypedFormGroup = new UntypedFormGroup({});
  @Input() controlName: UntypedFormControl = new UntypedFormControl();
  @Output('closeEvent') closeEvent: EventEmitter<any> = new EventEmitter();
  @Output('selectedRoom') selectedRoom: EventEmitter<any> = new EventEmitter();

  CreateRoomForm: UntypedFormGroup = new UntypedFormGroup({});
  disableSaveBtn: boolean = false;
  roomList$ = new BehaviorSubject<any[]>([]);
  constructor(
    private http: HttpClient,
    private store: Store,
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private RoomService: RoomService,
    private toastMessageService: ToastMessageService
  ) { }

  ngOnInit(): void {
    this.GetRooms();
    this.CreateRoomForm = this.fb.group({
      name_primary_language: ['', [Validators.required]],
      name_secondary_language: ['']
    });
  }

  ngAfterViewInit(): void {
    this.roomId$.subscribe((res) => {
      this.setSelectedRoom(res);
    });
  }

  GetRooms() {
    this.store.dispatch({ type: 'FETCH_ROOM_LIST' });
    this.store.select(ROOM_LIST_STATE).subscribe((responseData: Room[]) => {
      this.roomList$.next(responseData || []);
    });
  }

  reloadRoom(roomId: Number) {
    this.RoomService.GetRoomList(this.tenantId).subscribe((responseData: ListRoomResponse) => {
      this.roomList$.next(responseData.items || []);
      let defaultSelectedRoom = this.roomList$.value.find((o: Room) => o.id == roomId);
      if (defaultSelectedRoom && defaultSelectedRoom.id > 0) {
        this.controlName.setValue(defaultSelectedRoom);
      }
    });
  }

  setSelectedRoom(roomId: number): void {
    if (roomId > 0) {
      let defaultSelectedRoom = this.roomList$.value.find((o: Room) => o.id == roomId);
      if (defaultSelectedRoom && defaultSelectedRoom.id > 0) {
        this.controlName.setValue(defaultSelectedRoom);
      }
    }
  }

  onRoomChange(event: any) {
    this.selectedRoom.emit(event.value);
  }

  CreateRoom(form: any) {
    if (this.CreateRoomForm.valid) {
      this.disableSaveBtn = true;
      let createRoomRequest: CreateRoom = {} as CreateRoom;
      createRoomRequest.name_primary_language = form.value.name_primary_language;
      createRoomRequest.name_secondary_language = form.value.name_secondary_language;
      this.RoomService.CreateRoom(this.tenantId, createRoomRequest).subscribe({
        next: async (response: any) => {
          this.disableSaveBtn = false;
          this.addNewRoom = false;
          this.CreateRoomForm.reset();
          this.reloadRoom(response.id);
          this.closeEvent.emit({ closeRoomPopup: true, reloadData: true });
        },
        error: (error: any) => {
          this.disableSaveBtn = false;
          this.ShowToastErrors(error);
        },
      });
    }
    else {
      Object.keys(this.CreateRoomForm.controls).forEach((field) => {
        const control = this.CreateRoomForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }

  ShowToastErrors(error: any) {
    if (error.originalError.error.length > 0) {
      error.originalError.error.forEach((e: any) => {
        this.toastMessageService.showErrorMessage(e);
      });
    } else {
      this.toastMessageService.showErrorMessage(error);
    }
  }

  timeout(ms: any) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  closePopup() {
    this.addNewRoom = false;
    this.CreateRoomForm.reset();
    this.closeEvent.emit({ closeRoomPopup: true, reloadData: false });
  }

  get primaryNameFormControl(): UntypedFormControl {
    return this.CreateRoomForm.controls.name_primary_language as UntypedFormControl;
  }

}